// lib
import { useContext, useState, useRef, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import ADND from '@aytm/lib/adnd/ADND'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'
import { DataContext } from '@aytm/app/stats/page/sidebar/filters/FiltersPanel'

// components
import SidebarPopupLabel from '@aytm/app/stats/page/sidebar/components/SidebarPopupLabel'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

// styles
import style from '@aytm/app/stats/page/sidebar/filters/styles/FiltersPanel.module.scss'

const FiltersRowQuestionToggle = ({ group, filter, filterValues, collapsed, onToggle }) => {
  const { root, state, features, onDNDStart, onDNDEnd, addQuestion, onScrollToQuestion } = useContext(DataContext)

  const [restoreDisabled, setRestoreDisabled] = useState(false)

  const dndListItemRef = useRef()

  const filtersCount = useMemo(() => {
    if ('advanced' == state.mode || null === state.basic) return 0

    return filterValues.reduce((acc, cur) => {
      if (
        !cur.disabled &&
        state.basic[cur.key.toUpperCase()] != undefined &&
        0 < Object.keys(state.basic[cur.key.toUpperCase()]).length
      ) {
        return acc + 1
      }

      return acc
    }, 0)
  }, [filterValues, state.mode, state.basic])

  // The toggle itself stays live -- it only opens the sub-rows -- so it takes the disabled look of the
  // rows it opens, not their refusal to be clicked.
  const filtersDisabled =
    'advanced' == state.mode ||
    features.filters.disabled ||
    (0 < filterValues.length && filterValues.every(cur => cur.disabled))

  const fetchData =
    filter.draggable && !features.filters.disabled
      ? {
          key: (filter.id || filter.key).split('-')[1],
          model: null,
          windowUID: PageGlobals.WINDOW_UID
        }
      : null

  const dndListItem =
    filter.draggable && !features.filters.disabled
      ? ADND.sources
          .data({
            type: `${group.key}-${PageGlobals.WINDOW_UID}`,
            clearDragImage: false
          })
          .onStart(onDNDStart)
          .onEnd(onDNDEnd)
          .fetchData(() => fetchData)
      : null

  const onRestoreQuestion = () => {
    setRestoreDisabled(true)
    addQuestion(filter.id)
  }

  useEffect(() => {
    if (dndListItem) dndListItem.connect(dndListItemRef.current)

    return () => {
      if (dndListItem) dndListItem.disconnect()
    }
  }, [dndListItem])

  return (
    <div
      data-id={filter.key}
      data-testid={`stat-filters-${filter.key}`}
      data-type={filter.type}
      className={`${style['filter-row']} ${style[`offset-${filter.lvl || 0}`]}`}
    >
      <div ref={dndListItemRef} className={style['filter-row-inner']}>
        <div className="p-fluid p-col-12">
          <div
            data-testid={`stat-filters-${filter.key}-toggle`}
            data-filtering={0 < filtersCount}
            data-disabled={filtersDisabled}
            className={`${style['filter-button']}${
              filter.draggable && !features.filters.disabled ? ' p-composite-right' : ''
            }${0 < filtersCount ? ' p-inputwrapper-filled' : ''}${filtersDisabled ? ' ' + style['filter-button-dimmed'] : ''}`}
            {...pressable(() => onToggle(filter.id))}
          >
            {filter.icon ? <i className={`icon-aytmfd-${filter.icon}`} /> : null}
            <span className={style['filter-button-text']}>
              <SidebarPopupLabel root={root} text={filter.text} label={filter.title} />
            </span>
            <span
              className={classNames(style['filter-button-toggle'], {
                [style['filter-button-toggle-collapsed']]: collapsed
              })}
            >
              <i className="icon-aytmfd-chevron-bracket" />
            </span>
          </div>
        </div>
        {filter.draggable && !features.filters.disabled && (
          <span data-testid={`stat-filters-${filter.key}-handle`} className={style['filter-handle']}>
            <i className="icon-aytmfd-handle" />
          </span>
        )}
      </div>
      {filter.draggable && !features.filters.disabled && (
        <span
          data-testid={`stat-filters-${filter.key}-restore`}
          className={style['filter-restore']}
          aria-label="Restore question"
          title="Restore question"
          {...pressable(restoreDisabled ? null : onRestoreQuestion)}
        />
      )}
      {filter.scrollable && (
        <span
          data-id={filter.id}
          data-testid={`stat-filters-${filter.key}-scroll`}
          className={style['filter-scroll']}
          aria-label="Scroll to question"
          title="Scroll to question"
          {...pressable(() => onScrollToQuestion(filter.id))}
        />
      )}
    </div>
  )
}

FiltersRowQuestionToggle.propTypes = {
  group: PropTypes.object,
  filter: PropTypes.object,
  filterValues: PropTypes.array,
  collapsed: PropTypes.bool,
  onToggle: PropTypes.func
}

FiltersRowQuestionToggle.displayName = 'FiltersRowQuestionToggle'

export default FiltersRowQuestionToggle
